import { PRODUCT_METADATA } from "../../../utils/groceryConstants";

export function getAuthHeaders() {
  const token = localStorage.getItem("authToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function getLocalizedProductName(item, language) {
  let langMap = item?.lang ?? item?.product?.lang;
  if (typeof langMap === "string") {
    try {
      langMap = JSON.parse(langMap);
    } catch {
      langMap = null;
    }
  }
  if (langMap && typeof langMap === "object") {
    const exact = langMap[language];
    if (exact) return exact;
    const base = language?.split("-")[0];
    if (base && langMap[base]) return langMap[base];
  }
  return item?.displayName || item?.productName || "Unknown Product";
}

export function getProductImage(item) {
  if (item.unmatched === true || item.error) {
    return "/assets/products/no-image.jpg";
  }
  if (item.selectedSize?.image) {
    return item.selectedSize.image;
  }
  if (item.productId && PRODUCT_METADATA[item.productId]?.image) {
    return `/assets/products/${PRODUCT_METADATA[item.productId].image}`;
  }
  return "/assets/products/no-image.jpg";
}

/**
 * Return the first size entry of a raw Typesense product document, if any.
 */
export function getSearchDocSize(doc) {
  return Array.isArray(doc?.sizes) && doc.sizes.length > 0 ? doc.sizes[0] : null;
}

/**
 * Resolve a display image URL from a raw Typesense product document.
 */
export function getSearchDocImage(doc) {
  const size = getSearchDocSize(doc);
  return (
    size?.image ||
    doc?.thumbnail ||
    doc?.image ||
    "/assets/products/no-image.jpg"
  );
}

/**
 * Resolve a numeric price from a raw Typesense product document.
 * Returns null when no price is available.
 */
export function getSearchDocPrice(doc) {
  const size = getSearchDocSize(doc);
  const raw = size?.price ?? doc?.price ?? doc?.price_amount;
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Build a human-readable weight / pcs / unit label from a raw Typesense
 * product document. `t` is passed for localized unit strings.
 */
export function getSearchDocAmountLabel(doc, t) {
  const fmt = (n) => {
    const num = parseFloat(n);
    if (!Number.isFinite(num)) return n;
    return Number.isInteger(num) ? String(num) : num.toFixed(2);
  };
  const size = getSearchDocSize(doc);
  if (size) {
    if (size.weight && size.weightUnit) {
      return `${fmt(size.weight)} ${size.weightUnit}`;
    }
    if (size.itemsQty) {
      return `${size.itemsQty} ${t("grocery.units.pcs")}`;
    }
    if (size.type === "unit") {
      return t("grocery.units.piece_one");
    }
    if (size.description) {
      return size.description;
    }
  }
  if (doc?.weight && doc?.weightUnit) {
    return `${fmt(doc.weight)} ${doc.weightUnit}`;
  }
  return "";
}

/**
 * Translate a grocery category name using react-i18next's t() function.
 * `t` is passed explicitly so this util stays framework-neutral.
 */
export function translateCategory(category, t) {
  if (!category) return t("grocery.uncategorized");
  const c = category.toLowerCase();
  if (c.includes("produce")) return t("grocery.categories.produce");
  if (c.includes("meat") || c.includes("seafood"))
    return t("grocery.categories.meat");
  if (c.includes("dairy") || c.includes("eggs") || c.includes("alternatives"))
    return t("grocery.categories.dairy");
  if (c.includes("grains") || c.includes("pasta"))
    return t("grocery.categories.grains");
  if (c.includes("frozen")) return t("grocery.categories.frozen");
  if (c.includes("bakery")) return t("grocery.categories.bakery");
  if (c.includes("refrigerated")) return t("grocery.categories.refrigerated");
  if (c.includes("pantry")) return t("grocery.categories.pantry");
  if (c.includes("condiments")) return t("grocery.categories.condiments");
  if (c.includes("other")) return t("grocery.categories.other");
  return category;
}

/**
 * Map a grocery category name to a representative emoji icon.
 */
export function getCategoryIcon(cat) {
  if (!cat) return "📦";
  const c = cat.toLowerCase();
  if (c.includes("produce")) return "🥕";
  if (c.includes("meat & seafood")) return "🥩";
  if (c.includes("dairy, alternatives & eggs")) return "🥛";
  if (c.includes("grains & pasta")) return "🌾";
  if (c.includes("frozen")) return "🧊";
  if (c.includes("bakery")) return "🍞";
  if (c.includes("refrigerated")) return "❄️";
  if (c.includes("pantry")) return "🥫";
  if (c.includes("condiments")) return "🧂";
  if (c.includes("other")) return "📦";
  return "📦";
}
